import React, { useMemo, useState } from "react";
import { formatCurrency } from "../../../shared/helpers";
import "./subsidyDetailModal.css";

function SubsidyDetailModal({ pme, onClose, subsidyCodes }) {
    const [showAllCodes, setShowAllCodes] = useState(false);
    const subsidyNames = {
        general: 'General',
        sep: 'SEP',
        pie: 'PIE',
        eib: 'EIB',
        maintenance: 'Mantenimiento',
        retention: 'Pro Retención',
        internship: 'Internado',
        reinforcement: 'Reforzamiento',
        faep: 'FAEP'
    };

    // Verificar si existen datos de subvenciones
    if (!pme.incomeFromSubsidies) {
        return (
            <section className='subsidy-details-container flex'>
                <article className='modal-header flex'>
                    <h3>Detalle Completo de Ingresos por Subvenciones PME {pme.yearPME}</h3>
                    <button
                        className='button-close'
                        onClick={(e) => {
                            e.preventDefault();
                            onClose(false);
                        }}
                    >
                        <p>Cerrar</p>
                    </button>
                </article>
                <article className='modal-body flex'>
                    <p className=' message-not-found flex'>
                        No hay subvenciones ingresadas para este PME
                    </p>
                </article>
            </section>
        );
    };

    const subsidies = pme.incomeFromSubsidies;

    // Agrupa los códigos por tipo de subvención
    const getSubsidyCodes = (subsidyType, subsidyData, showAll) => {
        const codes = Object.entries(subsidyCodes)
            .filter(([_, data]) => data.allowedSubsidies.includes(subsidyType))
            .map(([code, data]) => ({
                code,
                name: data.name
            }))
            .sort((a, b) => a.code.localeCompare(b.code));

        if (!showAll) {
            // Obtener todos los códigos que tienen algún valor en cualquier mes
            const usedCodes = new Set();
            Object.values(subsidyData).forEach(monthData => {
                Object.keys(monthData).forEach(code => usedCodes.add(code));
            });
            return codes.filter(({ code }) => usedCodes.has(code));
        }

        return codes;
    };

    // Calcula los totales mensuales para una subvención específica
    const calculateMonthlyTotal = (subsidyData, month) => {
        if (!subsidyData || !subsidyData[month]) return 0;
        return Object.values(subsidyData[month]).reduce((sum, amount) => sum + (amount || 0), 0);
    };

    // Calcula el total anual para un código específico
    const calculateCodeTotal = (subsidyData, code) => {
        let total = 0;
        for (let month = 1; month <= 12; month++) {
            total += (subsidyData[month]?.[code] || 0);
        }
        return total;
    };

    const processedSubsidies = useMemo(() => {
        return Object.entries(subsidies).map(([subsidyKey, subsidyData]) => {
            const codes = getSubsidyCodes(subsidyKey, subsidyData, showAllCodes);
            const monthlyTotals = Array.from({ length: 12 }, (_, i) =>
                calculateMonthlyTotal(subsidyData, i + 1)
            );
            const grandTotal = monthlyTotals.reduce((sum, total) => sum + total, 0);

            return {
                name: subsidyNames[subsidyKey],
                key: subsidyKey,
                codes,
                data: subsidyData,
                monthlyTotals,
                grandTotal
            };
        });
    }, [subsidies, showAllCodes]);
    return (
        <section className='subsidy-details-container flex'>
            <article className='modal-header flex'>
                <h3>Detalle Completo de Ingresos por Subvenciones PME {pme.yearPME}</h3>
                <button
                    className='button-close'
                    onClick={(e) => {
                        e.preventDefault();
                        onClose(false);
                    }}
                >
                    <p>Cerrar</p>
                </button>
            </article>
            <article className='modal-body flex'>
                <article className='view-toggle-container flex'>
                    <label className='view-toggle flex'>
                        <input
                            type='checkbox'
                            checked={showAllCodes}
                            onChange={(e) => setShowAllCodes(e.target.checked)}
                        />
                        <span className='toggle-switch'></span>
                        <span className='toggle-label'>
                            Mostrar todos los códigos
                        </span>
                    </label>
                </article>
                {processedSubsidies.map(subsidy => (
                    <article key={subsidy.key} className='subsidy-table-container flex'>
                        <h4>{subsidy.name}</h4>
                        <span className='table-wrapper'>
                            <table className='subsidy-table'>
                                <thead>
                                    <tr>
                                        <th className='code-header'>Código</th>
                                        <th className='name-header'>Nombre</th>
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <th key={i + 1} className='month-header'>
                                                {new Date(0, i).toLocaleString('es', { month: 'short' })}
                                            </th>
                                        ))}
                                        <th className='total-header'>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subsidy.codes.map(({ code, name }) => {
                                        const codeTotal = calculateCodeTotal(subsidy.data, code);
                                        const months = [
                                            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                                            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                                        ];
                                        return (
                                            <tr key={code}>
                                                <td className='code-cell'>{code}</td>
                                                <td className='name-cell'>{name}</td>
                                                {months.map((month, index) => {
                                                    const monthNumber = index + 1;
                                                    const amount = subsidy.data[monthNumber]?.[code] || 0;
                                                    return (
                                                        <td
                                                            key={monthNumber}
                                                            data-month={month}
                                                        >
                                                            {formatCurrency(amount)}
                                                        </td>
                                                    );
                                                })}
                                                <td className='total-cell'>
                                                    {formatCurrency(codeTotal)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan='2' className='total-label'>Total Mensual:</td>
                                        {subsidy.monthlyTotals.map((total, index) => (
                                            <td key={index} className='total-cell'>
                                                {formatCurrency(total)}
                                            </td>
                                        ))}
                                        <td className='grand-total-cell'>
                                            {formatCurrency(subsidy.grandTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </span>
                    </article>
                ))}
            </article>
        </section>
    );
};

export default SubsidyDetailModal;